// Cliente de `nexa-api`, con el Bearer de Signa.
//
// REGLA CRÍTICA DE ESTE ARCHIVO (policies/resilience.md §4): **el cliente muestra la verdad del
// servidor.** El backend ya se toma el trabajo de responder `{error, retryable, step, fields}` con
// el paso que falló en lenguaje de persona (`nexa-api/src/lib/errors.ts`); taparlo con un «algo
// salió mal» propio no es una diferencia de estilo, es incumplir la política — y en Nexa además
// destruye lo único que la persona necesita saber para decidir si reintentar o corregir un campo.
//
// Corolario: `ApiError.message` es SIEMPRE el texto que vino del servidor cuando el servidor dijo
// algo. Los únicos mensajes propios de este módulo son para los dos casos en que no hubo mensaje
// que mostrar (la red no llegó, o la respuesta no era interpretable) — y esos dicen exactamente
// eso, no un genérico.

/** Un error de validación por campo. El 422 de `/v1/submissions` los devuelve así. */
export interface ApiFieldError {
  field: string;
  message: string;
}

/** El cuerpo de error del backend, tal cual sale de `toErrorBody()`. */
export interface ApiErrorBody {
  error: string;
  retryable?: boolean;
  step?: string;
  fields?: ApiFieldError[];
  details?: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  /** `true` ⇒ la UI ofrece reintentar. Lo decide el servidor, nunca el cliente. */
  readonly retryable: boolean;
  /** El paso del despacho que falló, cuando el servidor lo nombra. */
  readonly step?: string;
  /** Errores campo por campo de un 422: se devuelven al llamador para pintarlos en el formulario. */
  readonly fields?: ApiFieldError[];
  readonly details?: unknown;

  constructor(body: ApiErrorBody, status: number) {
    super(body.error);
    this.name = 'ApiError';
    this.status = status;
    this.retryable = body.retryable === true;
    if (body.step !== undefined) this.step = body.step;
    if (body.fields !== undefined) this.fields = body.fields;
    if (body.details !== undefined) this.details = body.details;
  }

  /** ¿Este error se pinta campo por campo en un formulario? */
  get hasFieldErrors(): boolean {
    return Array.isArray(this.fields) && this.fields.length > 0;
  }
}

export interface ApiClientOptions {
  origin: string;
  /** Devuelve el Bearer vigente (refrescándolo si hace falta). */
  getAccessToken: () => Promise<string>;
  /** Fuerza un refresco tras un 401. Devuelve el token nuevo. */
  refreshAccessToken: () => Promise<string>;
  /** Se llama cuando ni el refresco salva la sesión: el guard de ruta lleva a /login. */
  onSessionLost?: () => void;
  fetchImpl?: typeof fetch;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | undefined>;
  signal?: AbortSignal;
}

function buildUrl(origin: string, path: string, query?: RequestOptions['query']): string {
  const url = new URL(path.startsWith('/') ? path : `/${path}`, origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== '') url.searchParams.set(key, String(value));
  }
  return url.toString();
}

async function toApiError(res: Response): Promise<ApiError> {
  const text = await res.text().catch(() => '');
  try {
    const parsed = JSON.parse(text) as ApiErrorBody;
    if (parsed && typeof parsed.error === 'string') return new ApiError(parsed, res.status);
  } catch {
    // Cae al caso «no hubo mensaje interpretable», más abajo.
  }
  // Honestidad, no genericidad: se dice que el servidor no explicó nada y se muestra su código.
  // Un 5xx sin cuerpo es transitorio con más frecuencia que no, así que se ofrece el reintento.
  return new ApiError(
    { error: `El servidor respondió ${res.status} sin explicar qué pasó.`, retryable: res.status >= 500 },
    res.status,
  );
}

export class ApiClient {
  private readonly fetchImpl: typeof fetch;

  constructor(private readonly options: ApiClientOptions) {
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const token = await this.options.getAccessToken();
    let res = await this.send(path, options, token);

    // 401 ⇒ un refresco y UN reintento. Si el segundo también da 401, la sesión se terminó de
    // verdad: insistir sería un bucle contra el IdP.
    if (res.status === 401) {
      let refreshed: string;
      try {
        refreshed = await this.options.refreshAccessToken();
      } catch (err) {
        this.options.onSessionLost?.();
        throw err;
      }
      res = await this.send(path, options, refreshed);
      if (res.status === 401) this.options.onSessionLost?.();
    }

    if (!res.ok) throw await toApiError(res);
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  private async send(path: string, options: RequestOptions, token: string): Promise<Response> {
    const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';

    try {
      return await this.fetchImpl(buildUrl(this.options.origin, path, options.query), {
        method: options.method ?? 'GET',
        headers,
        ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
        ...(options.signal ? { signal: options.signal } : {}),
      });
    } catch (err) {
      // La red no llegó: no hay mensaje de servidor que mostrar, y se dice exactamente eso.
      // Es reintentable por definición — nadie decidió nada del otro lado.
      if (err instanceof DOMException && err.name === 'AbortError') throw err;
      throw new ApiError(
        { error: 'No pudimos comunicarnos con el servidor. Revisá tu conexión y reintentá.', retryable: true },
        0,
      );
    }
  }

  get<T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal): Promise<T> {
    return this.request<T>(path, { method: 'GET', ...(query ? { query } : {}), ...(signal ? { signal } : {}) });
  }

  post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: 'POST', ...(body !== undefined ? { body } : {}) });
  }

  put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: 'PUT', ...(body !== undefined ? { body } : {}) });
  }

  delete<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: 'DELETE' });
  }

  /**
   * Registros del sustrato para los selectores de relaciones. PASSTHROUGH: lo que Runa no le
   * muestra a esta persona, Nexa tampoco — el combobox no completa ni «arregla» nada.
   */
  async searchRecords(
    entity: string,
    search: string,
    signal?: AbortSignal,
  ): Promise<Array<Record<string, unknown>>> {
    const page = await this.get<{ data?: Array<Record<string, unknown>> }>(
      `/v1/records/${encodeURIComponent(entity)}`,
      { search: search || undefined, pageSize: 20 },
      signal,
    );
    return page.data ?? [];
  }
}

/** ¿Este error trae errores de campo para pintar? Azúcar para los `catch` de los formularios. */
export function fieldErrorsOf(err: unknown): ApiFieldError[] {
  return err instanceof ApiError && err.hasFieldErrors ? (err.fields as ApiFieldError[]) : [];
}

/** El mensaje que se muestra. Del servidor si lo hay; nunca un «algo salió mal» inventado. */
export function messageOf(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error && err.message) return err.message;
  return 'No pudimos completar la acción y no recibimos una explicación.';
}

export function isRetryable(err: unknown): boolean {
  return err instanceof ApiError && err.retryable;
}
